import * as Sentry from '@sentry/browser';
import type { SagaIterator } from 'redux-saga';
import { put, delay } from 'redux-saga/effects';
import { all as typedAll, call as typedCall, select as typedSelect } from 'typed-redux-saga';
import { selectProfileName, selectUserId } from 'flowhawk-core';

import { sendCommandAndGetResult, sendCommandNoResult } from './Socket';
import {
  setContacts,
  inviteNewUserSuccess,
  inviteNewUserFailed,
  resendNotificationSuccess,
  resendNotificationFailed,
  addSingleUserToChatroomSuccess,
  addSingleUserToChatroomFailed,
  resetContactsStatuses,
  receiveChatroomInvites,
} from '../Redux/ChatroomActions';
import { loadChatMembers, loadMessages, receiveRoomDoNotDisturb } from '../Redux/ChatListActions';
import type {
  AddUsersToChatroom,
  AddSingleUserToChatroom,
  RemoveUserFromChatroom,
  InviteNewUser,
  ToggleMute,
  MuteCase,
  SetNotificationSound,
  ResendNotification,
  DeleteChatroom,
  GetChatroomInvites,
  DeleteChatroomInvite,
} from '../Redux/ChatroomActions';

import type { RemovedFromChatroom } from '../Redux/ChatListActions';

import api from '../Services/Api';
import { apiFetch, apiPost, apiPut, apiDelete } from './ApiSaga';
import { singleErrorMessage } from '../Utils/flattenError';
import { pushError, setSagaMessage } from '../Redux/ApplicationActions';
import { startLoading, finishLoading } from '../Redux/LoadingActions';
import { alphaSortMemberData } from '../Utils/sort';
import type { RootState } from '../Redux';
import type {
  ApiChatroomMemberUpdateResponse,
  ApiContactsResponse,
  ApiGetChatroomInvitesResponse,
  ApiDeleteChatroomInviteResponse,
  ApiInviteUserRequest,
  ApiInviteUserResponse,
} from '../Utils/apiTypes';
import { ResourceErrorInterface } from 'tg-resources';
import type { ContactsMap, SetNotificationSoundPayload } from '../Utils/types';
import { parseChatroomInvite } from '../Utils/profile';
import type { CommandResult } from './Socket/Commands';

export function* toggleChatroomMute(action: ToggleMute): SagaIterator<void> {
  const { chatId } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const isMuted = yield* typedSelect((state: RootState) => state.chatList.chats[`${chatId}`].doNotDisturb);

  const { error, result } = yield* typedCall(
    apiPut<ApiChatroomMemberUpdateResponse>,
    api.txp.updateChatroomMember,
    { chatId },
    { do_not_disturb: !isMuted },
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isNetworkError) {
        yield put(pushError('Failed to mute room, are you online?', error));
      } else {
        yield put(pushError('Muting room failed, try again later', error));
      }
    } else {
      yield put(pushError('Muting room failed, try again later', error));
    }
  } else if (result.result === 'Success') {
    yield put(receiveRoomDoNotDisturb(chatId, !isMuted));
  }
}

/**
 * Mute or unmute a case. Currently this means (un)muting all chatrooms associated with the case.
 */
export function* muteCase(action: MuteCase) {
  const { caseId, mute } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const chatroomEntries = yield* typedSelect((state: RootState) => Object.entries(state.chatList.chats));
  const caseChatroomIds: number[] = chatroomEntries
    .filter(([_, chat]) => chat.donorId === caseId)
    .map(([_, chat]) => chat.id);

  const results = yield* typedAll(
    caseChatroomIds.map((chatId) =>
      typedCall(
        apiPut<ApiChatroomMemberUpdateResponse>,
        api.txp.updateChatroomMember,
        { chatId },
        { do_not_disturb: mute },
      ),
    ),
  );

  const errors = results.filter((r) => r.error).map((r) => r.error);

  if (errors.length > 0) {
    const [error] = errors;
    let errorMessage = `Something went wrong while ${mute ? '' : 'un'}muting case rooms`;

    if (error instanceof ResourceErrorInterface) {
      if (error.isNetworkError) {
        errorMessage = `Failed to ${mute ? '' : 'un'}mute case rooms, are you online?`;
      } else {
        errorMessage = `${mute ? 'Muting' : 'Unmuting'} case rooms failed, try again later`;
      }
    }
    yield put(pushError(errorMessage, error));
    yield put(setSagaMessage('', errorMessage, ''));
  }

  for (let i = 0; i < results.length; i += 1) {
    const r = results[i].result;
    if (r && r.result === 'Success') {
      yield put(receiveRoomDoNotDisturb(r.chatroom_id, mute));
    }
  }
}

export function* setNotificationSound(action: SetNotificationSound): SagaIterator<void> {
  const { chatId, notificationSound, isPriority } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const payloadKey = isPriority ? 'high_priority_alert_sound' : 'alert_sound';
  const payload: SetNotificationSoundPayload = {};
  payload[payloadKey] = notificationSound;
  const { error, result } = yield* typedCall(
    apiPut<ApiChatroomMemberUpdateResponse>,
    api.txp.updateChatroomMember,
    { chatId },
    payload,
  );

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isNetworkError) {
        yield put(pushError('Failed to save notification sound, are you online?', error));
      } else {
        yield put(pushError('Saving notification sound failed, try again later', error));
      }
    } else {
      yield put(pushError('Saving notification sound failed, try again later', error));
    }
  } else if (result.result === 'Success') {
    // Reload chatrooms to sync server and local data
    // Future optimization: only load the new room
    yield put(loadChatMembers(chatId));
  }
}

export function* inviteNewUser(action: InviteNewUser): SagaIterator<void> {
  const { chatId, memberId, email, phone, firstName, lastName, organization, organizationId, department } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  // use an unsealed object to prevent flow errors in adding either phone or both
  const profileName = yield* typedSelect((state: RootState) =>
    state.auth.profile ? selectProfileName(state.auth.profile) : 'FlowHawk User',
  );
  const payload: ApiInviteUserRequest = {
    chatroom_id: chatId,
    sender_information: profileName,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
    organization: organization || undefined,
    department: department || undefined,
    org_id: organizationId,
  };

  payload.email = email || undefined;
  payload.phone = phone || undefined;
  const { error } = yield* typedCall(apiPost<ApiInviteUserResponse>, api.txp.inviteNewUser, undefined, payload);

  if (error) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isNetworkError) {
        yield put(inviteNewUserFailed('Request failed - are you offline?'));
      } else {
        // Probably a malformed request in some way.
        yield put(inviteNewUserFailed('An unexpected error occurred.'));
      }
    } else {
      // Probably a malformed request in some way.
      yield put(inviteNewUserFailed('An unexpected error occurred.'));
    }
  } else {
    yield put(inviteNewUserSuccess());
    yield put(setSagaMessage('New user invite sent!', '', ''));
    yield put(loadMessages(chatId, memberId, true, 0));

    // Re-fetch chatroom invites so the new invite appears in the list
    const { result: invitesResult } = yield* typedCall(
      apiFetch<ApiGetChatroomInvitesResponse>,
      api.txp.chatroomInvites,
      { chatId },
    );
    if (invitesResult) {
      const invites = invitesResult.map((invite) => parseChatroomInvite(invite));
      yield put(receiveChatroomInvites(chatId, invites));
    }
  }
}

export function* getContactsSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('contacts'));

  const { result, error } = yield* typedCall(apiPost<ApiContactsResponse>, api.txp.getContacts, undefined, {
    email_list: [],
  });

  if (error || !result) {
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        const errorMessage = 'Failed to get contacts';
        yield put(pushError(`${errorMessage}`));
      } else if (error.isNetworkError) {
        Sentry.captureException(error, { extra: { errorMsg: 'GetContactsSaga Failed - Network Error' } });
      } else {
        Sentry.captureException(error, { extra: { errorMsg: 'GetContactsSaga Failed - Other Error' } });
      }
    } else {
      Sentry.captureException(error, { extra: { errorMsg: 'GetContactsSaga Failed - Other Error' } });
    }

    yield put(finishLoading('contacts'));
  } else {
    const contactsMap: ContactsMap = {};
    const contactsOrder: number[] = [];

    // Make sure the arrays are defined.
    const chatroomContacts = result.chatroom_contacts || [];
    const organizationContacts = result.organization_contacts || [];
    const publicContacts = result.public_contacts || [];

    // Get chatroom array, org array, and public array and remove duplicates
    const allContacts = chatroomContacts.concat(organizationContacts, publicContacts);
    for (let i = 0; i < allContacts.length; i += 1) {
      for (let j = i + 1; j < allContacts.length; j += 1) {
        if (allContacts[i].email === allContacts[j].email) {
          j -= 1;
          allContacts.splice(j, 1);
        }
      }
    }

    const memberDataList = allContacts.map((contact) => ({
      profile: {
        firstName: contact.first_name,
        lastName: contact.last_name,
        email: contact.email,
        phone: contact.phone,
        profilePicture: contact.presigned_profile_picture_url,
        profilePictureThumbnail: contact.presigned_profile_picture_thumbnail_url,
        userId: contact.user_id,
        // explicitly setting this because otherwise 'No Organization' is displayed, which isn't always true
        organizationName: ' ',
        organizationalRole: contact.organizational_role,
        department: contact.department,
      },
      membershipStatus: undefined,
    }));

    const sortedMemberData = alphaSortMemberData(memberDataList);
    sortedMemberData.forEach((memberData) => {
      contactsOrder.push(memberData.profile.userId);
      contactsMap[memberData.profile.userId] = memberData;
    });
    yield put(setContacts(contactsMap, contactsOrder));

    yield put(finishLoading('contacts'));
  }
}

export function* addSingleUserToChatroom(action: AddSingleUserToChatroom) {
  const { chatId, memberId, email, phone } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  // Clear the status
  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'add-user-v2', [email, phone]);

  if (ack && ack.result) {
    // The message worked. User will be added when member list is reloaded below
    yield put(addSingleUserToChatroomSuccess());
    // The chatroom membership has changed so reload the chat members - #DontTryToDelta
    yield put(loadChatMembers(chatId));
    // Messages will have been created by the process so reload them - #KIS
    yield put(loadMessages(chatId, memberId, true, 0));
  } else {
    if (ack && ack.error.error) {
      yield put(addSingleUserToChatroomFailed(ack.error.error));
    } else if (ack && ack.error.type === 'not-txp-user') {
      yield put(addSingleUserToChatroomFailed('Not a registered user'));
    } else {
      yield put(addSingleUserToChatroomFailed('Invalid phone or email.'));
    }
    // Use null guard pattern to ensure ack && ack.error exists
    // NOTE: could get here without an ack.result too, might be worth a refactor...
    Sentry.captureException(
      ack && ack.error ? ack.error : new Error('addSingleUserToChatroom Failed (no ack, or ack.result)'),
      {
        extra: {
          chatId,
          memberId,
          emailToAdd: email,
          phoneToAdd: phone,
          call: 'add-user-v2',
        },
      },
    );
  }
}

export function* addUsersToChatroom(action: AddUsersToChatroom) {
  const { chatId, memberId, users } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const usersAdded: { userId: number; email: string; phone: string }[] = [];
  users.forEach((user) => {
    // compose object with id, email and phone here
    const userInfo = {
      userId: user.userId,
      email: user.email,
      phone: user.phone,
    };

    usersAdded.push(userInfo);
  });

  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'add-multiple-users', [usersAdded]);

  if (!ack || ack.error) {
    Sentry.captureException(ack ? ack.error : new Error('addUsersToChatroom Failed (no ack)'), {
      extra: {
        chatId,
        memberId,
        usersAdded,
        call: 'add-multiple-users',
      },
    });
  }

  // The chatroom membership has changed so reload the chat members - #DontTryToDelta
  yield put(loadChatMembers(chatId));
  // Messages will have been created by the process so reload them - #KIS
  yield put(loadMessages(chatId, memberId, true, 0));
  // reset org member statuses
  yield put(resetContactsStatuses());
}

export function* removeUserFromChatroom(action: RemoveUserFromChatroom) {
  const { chatId, email } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'remove-member-v2', [email]);
}

export function* deleteChatroom(action: DeleteChatroom) {
  const { chatId } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const memberId = yield* typedSelect((state: RootState) => selectUserId(state.auth));
  yield sendCommandNoResult(chatId, memberId, 'delete-chatroom', [chatId]);
}

export function* handleDeletedChatroom(action: RemovedFromChatroom): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(400);

  const { roomId } = action;

  // Notify the user about the removal
  const roomName = yield* typedSelect(
    (state) => state.chatList.chats[`${roomId}`] && state.chatList.chats[`${roomId}`].name,
  );
  yield put(setSagaMessage('Room:', roomName, 'has been archived.'));

  // yield put(setMessageText(roomId, ''));
  // yield put(setMessageMedia(roomId, []));
  // yield put(setMentionedUsers(roomId, []));
  // yield put(setMentionedNames(roomId, []));
  // yield put(closeChatroom(roomId));

  // Remove chatroom from redux
  // yield put(removeChatroom(roomId));
}

export function* resendNotification(action: ResendNotification) {
  const memberId = yield* typedSelect((state) => selectUserId(state.auth));
  const { chatId, userId, messageId } = action;
  yield delay(50);

  const ack: CommandResult = yield sendCommandAndGetResult(chatId, memberId, 'resend-notification', [
    userId,
    messageId,
  ]);

  if (ack && ack.result) {
    yield put(resendNotificationSuccess(userId, messageId));
    yield put(setSagaMessage('Notification Sent', '', ''));
  } else {
    yield put(resendNotificationFailed(singleErrorMessage(ack.error)));
    yield put(setSagaMessage('Failed to resend notification', '', ''));
    Sentry.captureException(ack && ack.error ? ack.error : new Error('Resend Notification Failed'), {
      extra: { chatId, messageId, call: 'resend-notification' },
    });
  }
}

export function* getChatroomInvitesSaga(action: GetChatroomInvites): SagaIterator<void> {
  const { chatroomId } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const { result, error } = yield* typedCall(apiFetch<ApiGetChatroomInvitesResponse>, api.txp.chatroomInvites, {
    chatId: chatroomId,
  });

  if (error || !result) {
    yield put(pushError('Failed to get chatroom invites', error));
  } else {
    const invites = result.map((invite) => parseChatroomInvite(invite));
    yield put(receiveChatroomInvites(chatroomId, invites));
  }
}

export function* deleteChatroomInviteSaga(action: DeleteChatroomInvite): SagaIterator<void> {
  const { chatroomId, chatroomInviteId } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const { result, error } = yield* typedCall(apiDelete<ApiDeleteChatroomInviteResponse>, api.txp.deleteChatroomInvite, {
    chatId: chatroomId,
    chatroomInviteId,
  });

  if (error || !result) {
    yield put(pushError('Failed to remove chatroom invite', error));
    yield put(setSagaMessage('', 'Failed to remove invited user', ''));
  } else {
    const invites = result.map((invite) => parseChatroomInvite(invite));
    yield put(receiveChatroomInvites(chatroomId, invites));
  }
}
